"use client";

import { JSX, useMemo, useState } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { CascadeIn } from "@/components/motion/cascade-in";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import type { ModuleListCardItem, ModuleListCardsData } from "@/types/modules";
import { renderInlineMarkdown, stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import { resolveCategories, resolveCategoryColor } from "./shared/category-resolve";

const SCROLL_MASK =
  "w-full [mask-image:linear-gradient(to_right,transparent,black_2%,black_98%,transparent)]";

const ALL = "__all__";

type CardGroup = {
  id: string;
  label: string;
  color: string | null;
  cards: ModuleListCardItem[];
};

function Card({
  card,
  accent,
}: {
  card: ModuleListCardItem;
  accent: string | null;
}): JSX.Element {
  return (
    <div
      className={cn(
        "bg-[var(--card)] rounded-xl ring-1 ring-inset ring-black/[0.08] dark:ring-white/[0.06]",
        "overflow-hidden transition-all duration-200",
        "hover:ring-black/[0.14] dark:hover:ring-white/[0.10]",
      )}
    >
      <div
        className="relative h-28 flex items-center justify-center text-[var(--module-accent)]"
        style={accent ? { color: accent } : undefined}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(var(--grid) 0.8px, transparent 0.8px)",
            backgroundSize: "14px 14px",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
            maskImage:
              "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
          }}
        />
        <div className="relative">
          <AnimatedIcon name={card.icon} className="size-7 stroke-[1.3]" />
        </div>
      </div>

      <div className="border-t border-dashed border-[var(--border-dashed)]" />

      <div className="p-6">
        <h3
          className={`text-[15px] font-medium text-[var(--fg)] mb-1.5 tracking-tight ${MODULE_LABEL_WRAP.heading}`}
        >
          {stripInlineMarkdown(card.heading)}
        </h3>
        <p
          className={`text-sm text-[var(--muted)] leading-relaxed ${MODULE_LABEL_WRAP.body}`}
        >
          {renderInlineMarkdown(card.text)}
        </p>
      </div>
    </div>
  );
}

export default function ModuleListCards({
  data,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleListCardsData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const restLabel = t("Other");
  const cards = useMemo(() => data?.cards ?? [], [data?.cards]);
  const resolved = useMemo(
    () => resolveCategories(data?.categories, []),
    [data?.categories],
  );
  const free = resolved.free;

  const groups = useMemo<CardGroup[]>(() => {
    if (!free) return [];
    const knownIds = new Set(resolved.categories.map((cat) => cat.id));
    const known = resolved.categories.map((cat) => ({
      id: cat.id,
      label: cat.label,
      color: cat.color,
      cards: cards.filter((c) => c.categoryId === cat.id),
    }));
    const rest = cards.filter(
      (c) => !c.categoryId || !knownIds.has(c.categoryId),
    );
    return [
      ...known.filter((g) => g.cards.length > 0),
      ...(rest.length > 0
        ? [{ id: "__rest", label: restLabel, color: null, cards: rest }]
        : []),
    ];
  }, [free, cards, resolved, restLabel]);

  const [active, setActive] = useState<string>(ALL);

  const shown = useMemo(
    () =>
      active === ALL
        ? cards
        : (groups.find((g) => g.id === active)?.cards ?? cards),
    [active, cards, groups],
  );

  if (!free) {
    return (
      <SwipeScroll className={cn(SCROLL_MASK, className)}>
        <div style={importsTokenScope(grayscale, accent)}>
          <CascadeIn
            keySignature="list-cards"
            className="flex w-max flex-row gap-4 pr-10"
            itemClassName="w-[280px] shrink-0"
          >
            {cards.map((card, i) => (
              <div key={card.id ?? i}>
                <Card card={card} accent={null} />
              </div>
            ))}
          </CascadeIn>
        </div>
      </SwipeScroll>
    );
  }

  return (
    <div
      className={cn("flex w-full min-w-0 flex-col gap-4", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      {groups.length > 1 && (
        <div className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <SegmentTabs
            value={active}
            onValueChange={(v) => v !== null && setActive(v)}
            options={[
              { value: ALL, label: t("All"), count: cards.length },
              ...groups.map((group) => ({
                value: group.id,
                label: stripInlineMarkdown(group.label),
                count: group.cards.length,
                leading: group.color ? (
                  <span
                    className="size-1.5 rounded-full"
                    style={{ background: group.color }}
                  />
                ) : undefined,
              })),
            ]}
          />
        </div>
      )}
      <SwipeScroll className={SCROLL_MASK}>
        <CascadeIn
          keySignature={`list-cards-${active}`}
          className="flex w-max flex-row gap-4 pr-10"
          itemClassName="w-[280px] shrink-0"
        >
          {shown.map((card, i) => (
            <div key={card.id ?? i}>
              <Card
                card={card}
                accent={resolveCategoryColor(
                  card.categoryId,
                  resolved.categories,
                  "var(--module-accent)",
                )}
              />
            </div>
          ))}
        </CascadeIn>
      </SwipeScroll>
    </div>
  );
}
